import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../api';
import { urlEliminazione, messaggioEliminazione, avvisaConflitto } from '../../utils/eliminazione';
import Layout from '../../components/Layout';
import stili from '../../components/competenze/stili';
import ConflittoVersione, { eConflitto } from '../../components/ConflittoVersione';
import { useAuth } from '../../context/AuthContext';
import { formattaEuro } from '../../utils/importi';
import { formattaData } from '../../utils/date';

const SOGGETTI = { societa: 'Società (di capitali e di persone)', ditta_individuale: 'Ditta individuale', altre_pratiche: 'Altre pratiche (ogni soggetto)' };
const SOGGETTI_BREVI = { societa: 'Società', ditta_individuale: 'Ditta individuale', altre_pratiche: 'Altre pratiche' };

async function scarica(url, nomeFile) {
  const res = await api.get(url, { responseType: 'blob' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(res.data);
  link.download = nomeFile;
  link.click();
  setTimeout(() => URL.revokeObjectURL(link.href), 1000);
}

// Listino delle pratiche (prezzi IVA esclusa per tipo soggetto) e collegamento con le diciture.
// Lettura per chi ha Competenze; modifica, Excel e collegamenti solo per l'admin.
export default function ListinoPratiche() {
  const { utente } = useAuth();
  const admin = Boolean(utente?.admin);
  const [scheda, setScheda] = useState('listino');
  return (
    <Layout>
      <div style={stili.topBar}>
        <h2 style={{ margin: 0 }}>Listino pratiche</h2>
        <div style={{ display: 'flex', gap: '0.4rem' }}>
          <button onClick={() => setScheda('listino')} style={scheda === 'listino' ? stili.btn : stili.btnSecondary}>Prezzi</button>
          <button onClick={() => setScheda('collegamenti')} style={scheda === 'collegamenti' ? stili.btn : stili.btnSecondary}>Collegamento con le diciture</button>
        </div>
      </div>
      {scheda === 'listino' ? <Prezzi admin={admin} /> : <Collegamenti admin={admin} />}
    </Layout>
  );
}

// ---------- prezzi

function Prezzi({ admin }) {
  const [righe, setRighe] = useState([]);
  const [filtri, setFiltri] = useState({ cerca: '', soggetto: '', categoria: '' });
  const [inModifica, setInModifica] = useState(null); // 'nuovo' | riga
  const [voceInModifica, setVoceInModifica] = useState(null);
  const [importa, setImporta] = useState(false);
  const [errore, setErrore] = useState('');
  const [messaggio, setMessaggio] = useState('');

  const carica = useCallback(() => {
    api.get('/competenze/listino').then(r => setRighe(r.data.listino)).catch(err => setErrore(err.response?.data?.errore || 'Errore nel caricamento'));
  }, []);
  useEffect(() => { carica(); }, [carica]);

  const categorie = useMemo(() => [...new Set(righe.map(r => r.categoria).filter(Boolean))].sort(), [righe]);
  const q = filtri.cerca.trim().toLowerCase();
  const visibili = righe.filter(r => (!q || r.voce.toLowerCase().includes(q) || (r.note || '').toLowerCase().includes(q))
    && (!filtri.soggetto || r.soggetto === filtri.soggetto) && (!filtri.categoria || r.categoria === filtri.categoria));

  const elimina = async (r) => {
    if (!window.confirm(`Spostare nel Cestino il prezzo "${r.voce}" per ${SOGGETTI_BREVI[r.soggetto].toLowerCase()}? Le righe già salvate non cambiano.`)) return;
    try { await api.delete(urlEliminazione(`/competenze/listino/${r.id}`, r.version)); carica(); } catch (err) { setErrore(avvisaConflitto(err, messaggioEliminazione(err))); if (eConflitto(err)) carica(); }
  };

  return (
    <>
      <p style={{ color: '#555', fontSize: '0.88rem', marginTop: 0 }}>
        Prezzi fissi IVA esclusa per tipo soggetto del cliente. Quando una competenza usa una dicitura collegata a una voce, l'importo si compila da solo e si salva con la riga (resta modificabile).
        Se cambi un prezzo le righe già salvate non cambiano. Diritti di segreteria e bollo non fanno parte del listino.
      </p>
      {errore && <div style={stili.errore}>{errore}</div>}
      {messaggio && <div style={{ background: '#ecfdf5', color: '#166534', padding: '0.6rem', borderRadius: '6px', marginBottom: '0.8rem', fontSize: '0.88rem' }}>{messaggio}</div>}
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.8rem', alignItems: 'center' }}>
        <input type="search" placeholder="Cerca voce o nota…" value={filtri.cerca} onChange={e => setFiltri(f => ({ ...f, cerca: e.target.value }))} style={{ ...stili.input, flex: '2 1 220px', width: 'auto' }} />
        <select value={filtri.soggetto} onChange={e => setFiltri(f => ({ ...f, soggetto: e.target.value }))} style={{ ...stili.input, width: 'auto' }} aria-label="Soggetto">
          <option value="">Tutti i soggetti</option>
          {Object.entries(SOGGETTI_BREVI).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={filtri.categoria} onChange={e => setFiltri(f => ({ ...f, categoria: e.target.value }))} style={{ ...stili.input, width: 'auto' }} aria-label="Categoria">
          <option value="">Tutte le categorie</option>
          {categorie.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <button onClick={() => scarica('/competenze/listino/export/excel', 'listino_pratiche.xlsx')} style={stili.btnSecondary}>Esporta Excel</button>
        {admin && <button onClick={() => setImporta(true)} style={stili.btnSecondary}>Importa Excel</button>}
        {admin && <button onClick={() => setInModifica('nuovo')} style={stili.btn}>+ Nuovo prezzo</button>}
      </div>
      <table style={{ ...stili.table, tableLayout: 'fixed' }} className="tabellaCompatta">
        <thead>
          <tr>
            <th style={{ ...stili.th, width: '24%' }}>Voce</th>
            <th style={{ ...stili.th, width: '11%' }}>Categoria</th>
            <th style={{ ...stili.th, width: '11%' }}>Soggetto</th>
            <th style={{ ...stili.th, width: '9%' }}>Importo (IVA esclusa)</th>
            <th style={{ ...stili.th, width: '8%' }}>Valido dal</th>
            <th style={{ ...stili.th, width: '13%' }}>Note</th>
            <th style={{ ...stili.th, width: '12%' }}>Dicitura collegata</th>
            {admin && <th style={{ ...stili.th, width: '12%' }}>Azioni</th>}
          </tr>
        </thead>
        <tbody>
          {visibili.map(r => (
            <tr key={r.id} style={stili.tr}>
              <td style={stili.td}>{r.voce}</td>
              <td style={stili.td}>{r.categoria}</td>
              <td style={stili.td}>{SOGGETTI_BREVI[r.soggetto]}</td>
              <td style={stili.td}>{r.importo === null ? <span style={{ color: '#c2410c', fontSize: '0.78rem' }}>nessun importo automatico</span> : <strong>{formattaEuro(r.importo)} €</strong>}</td>
              <td style={stili.td}>{r.valido_dal ? formattaData(r.valido_dal) : <span style={{ color: '#999' }}>da sempre</span>}</td>
              <td style={{ ...stili.td, color: '#666' }}>{r.note}</td>
              <td style={stili.td}>{r.diciture.length ? r.diciture.map(d => <div key={d.id}>{d.testo}</div>) : <span style={{ color: '#999' }}>—</span>}</td>
              {admin && (
                <td style={stili.td}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', alignItems: 'flex-start' }}>
                    <button onClick={() => setInModifica(r)} style={{ ...stili.btnSmall, marginRight: 0 }}>Prezzo</button>
                    <button onClick={() => setVoceInModifica(r)} style={{ ...stili.btnSmall, marginRight: 0 }}>Rinomina</button>
                    <button onClick={() => elimina(r)} style={stili.btnSmallDanger}>Elimina</button>
                  </div>
                </td>
              )}
            </tr>
          ))}
          {visibili.length === 0 && <tr><td colSpan={admin ? 8 : 7} style={{ ...stili.td, textAlign: 'center', color: '#999' }}>Nessuna voce per questi filtri</td></tr>}
        </tbody>
      </table>
      <p style={{ color: '#555', fontSize: '0.88rem' }}>{visibili.length} prezzi · {new Set(visibili.map(r => r.voce_id)).size} voci</p>
      {inModifica && <FormPrezzo riga={inModifica === 'nuovo' ? null : inModifica} voci={[...new Set(righe.map(r => r.voce))]} categorie={categorie} onChiudi={() => setInModifica(null)} onSalvato={() => { setInModifica(null); setMessaggio('Prezzo salvato'); carica(); }} />}
      {voceInModifica && <FormVoce riga={voceInModifica} onChiudi={() => setVoceInModifica(null)} onSalvato={() => { setVoceInModifica(null); setMessaggio('Voce aggiornata'); carica(); }} />}
      {importa && <ImportaListino onChiudi={() => setImporta(false)} onFatto={(t) => { setImporta(false); setMessaggio(t); carica(); }} />}
    </>
  );
}

function FormPrezzo({ riga, voci, categorie, onChiudi, onSalvato }) {
  const daRiga = r => ({ voce: r?.voce || '', categoria: r?.categoria || '', soggetto: r?.soggetto || 'societa', importo: r && r.importo !== null ? formattaEuro(r.importo) : '', valido_dal: r?.valido_dal || '', note: r?.note || '', version: r?.version });
  const [form, setForm] = useState(daRiga(riga));
  const [errore, setErrore] = useState('');
  const [conflitto, setConflitto] = useState(null);
  const salva = async (version) => {
    setErrore('');
    try {
      if (riga) await api.put(`/competenze/listino/${riga.id}`, { soggetto: form.soggetto, importo: form.importo, valido_dal: form.valido_dal, note: form.note, version });
      else await api.post('/competenze/listino', form);
      onSalvato();
    } catch (err) {
      if (eConflitto(err)) setConflitto(err.response.data);
      else setErrore(err.response?.data?.errore || 'Errore nel salvataggio');
    }
  };
  const campo = (nome, etichetta, extra = {}) => (
    <div style={stili.campo}>
      <label style={stili.label}>{etichetta}</label>
      <input value={form[nome]} onChange={e => setForm(f => ({ ...f, [nome]: e.target.value }))} style={stili.input} {...extra} />
    </div>
  );
  return (
    <div style={stili.overlay}>
      <div style={{ ...stili.modal, maxWidth: '520px' }}>
        <h3 style={{ marginTop: 0 }}>{riga ? 'Modifica prezzo' : 'Nuovo prezzo'}</h3>
        <form onSubmit={e => { e.preventDefault(); salva(form.version); }}>
          {errore && <div style={stili.errore}>{errore}</div>}
          {riga ? <p style={{ marginTop: 0 }}><strong>{riga.voce}</strong></p> : (
            <>
              {campo('voce', 'Voce (una voce esistente o un nome nuovo)', { list: 'voci-listino', required: true, autoFocus: true })}
              <datalist id="voci-listino">{voci.map(v => <option key={v} value={v} />)}</datalist>
              {campo('categoria', 'Categoria (per una voce nuova)', { list: 'categorie-listino' })}
              <datalist id="categorie-listino">{categorie.map(c => <option key={c} value={c} />)}</datalist>
            </>
          )}
          <div style={stili.campo}>
            <label style={stili.label}>Tipo soggetto</label>
            <select value={form.soggetto} onChange={e => setForm(f => ({ ...f, soggetto: e.target.value }))} style={stili.input}>
              {Object.entries(SOGGETTI).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div style={stili.row}>
            {campo('importo', 'Importo (€, IVA esclusa)', { inputMode: 'decimal', placeholder: 'vuoto = nessun importo automatico' })}
            <div style={stili.campo}>
              <label style={stili.label}>Valido dal (facoltativo)</label>
              <input type="date" value={form.valido_dal} onChange={e => setForm(f => ({ ...f, valido_dal: e.target.value }))} style={stili.input} />
            </div>
          </div>
          {campo('note', 'Note')}
          <div style={stili.azioni}>
            <button type="button" onClick={onChiudi} style={stili.btnSecondary}>Annulla</button>
            <button type="submit" style={stili.btn}>Salva</button>
          </div>
        </form>
      </div>
      {conflitto && <ConflittoVersione conflitto={conflitto} onRicarica={() => { setForm(daRiga(conflitto.attuale)); setConflitto(null); }} onSovrascrivi={() => salva(conflitto.attuale.version)} />}
    </div>
  );
}

function FormVoce({ riga, onChiudi, onSalvato }) {
  const [form, setForm] = useState({ nome: riga.voce, categoria: riga.categoria || '', version: riga.voce_version });
  const [errore, setErrore] = useState('');
  const [conflitto, setConflitto] = useState(null);
  const salva = async (version) => {
    try { await api.put(`/competenze/listino/voci/${riga.voce_id}`, { nome: form.nome, categoria: form.categoria, version }); onSalvato(); } catch (err) {
      if (eConflitto(err)) setConflitto(err.response.data); else setErrore(err.response?.data?.errore || 'Errore nel salvataggio');
    }
  };
  return (
    <div style={stili.overlay}>
      <div style={{ ...stili.modal, maxWidth: '480px' }}>
        <h3 style={{ marginTop: 0 }}>Rinomina voce</h3>
        <p style={{ fontSize: '0.85rem', color: '#666', marginTop: 0 }}>Il nuovo nome vale per tutti i prezzi della voce e per le diciture collegate.</p>
        <form onSubmit={e => { e.preventDefault(); salva(form.version); }}>
          {errore && <div style={stili.errore}>{errore}</div>}
          <div style={stili.campo}><label style={stili.label}>Nome</label><input autoFocus required value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} style={stili.input} /></div>
          <div style={stili.campo}><label style={stili.label}>Categoria</label><input value={form.categoria} onChange={e => setForm(f => ({ ...f, categoria: e.target.value }))} style={stili.input} /></div>
          <div style={stili.azioni}>
            <button type="button" onClick={onChiudi} style={stili.btnSecondary}>Annulla</button>
            <button type="submit" style={stili.btn}>Salva</button>
          </div>
        </form>
      </div>
      {conflitto && <ConflittoVersione conflitto={conflitto} onRicarica={() => { setForm(f => ({ ...f, nome: conflitto.attuale.nome, categoria: conflitto.attuale.categoria || '', version: conflitto.attuale.version })); setConflitto(null); }} onSovrascrivi={() => salva(conflitto.attuale.version)} />}
    </div>
  );
}

// ---------- importazione da Excel con anteprima

const ESITI = { nuova: ['Nuova', '#166534', '#ecfdf5'], uguale: ['Uguale', '#555', '#f3f3f1'], diversa: ['Diversa', '#92400e', '#fef3c7'], non_riconosciuta: ['Non riconosciuta', '#b91c1c', '#fef2f2'] };

function ImportaListino({ onChiudi, onFatto }) {
  const [righe, setRighe] = useState(null);
  const [scelte, setScelte] = useState({});
  const [errore, setErrore] = useState('');
  const [inCorso, setInCorso] = useState(false);

  const leggi = async (file) => {
    setErrore('');
    setInCorso(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/competenze/listino/import/anteprima', fd);
      setRighe(r.data.righe);
      setScelte(Object.fromEntries(r.data.righe.map((x, i) => [i, x.esito === 'nuova'])));
    } catch (err) { setErrore(err.response?.data?.errore || 'Lettura non riuscita'); } finally { setInCorso(false); }
  };
  const applica = async () => {
    setErrore('');
    setInCorso(true);
    try {
      const r = await api.post('/competenze/listino/import/conferma', { righe: righe.filter((x, i) => scelte[i]) });
      onFatto(`Importazione fatta: ${r.data.create} prezzi nuovi, ${r.data.aggiornate} aggiornati`);
    } catch (err) { setErrore(err.response?.data?.errore || 'Importazione non riuscita: non è stato salvato niente'); setInCorso(false); }
  };
  const scelti = righe ? righe.filter((x, i) => scelte[i]).length : 0;

  return (
    <div style={stili.overlay}>
      <div style={{ ...stili.modal, maxWidth: '900px' }}>
        <h3 style={{ marginTop: 0 }}>Importa il listino da Excel</h3>
        <p style={{ fontSize: '0.85rem', color: '#666', marginTop: 0 }}>Colonne: Voce, Categoria, Soggetto (Società / Ditta individuale / Altre pratiche), Importo (IVA esclusa), Valido dal, Note. Prima vedi l'anteprima: non si scrive niente finché non confermi, e le righe "diverse" si applicano solo se le spunti.</p>
        {errore && <div style={stili.errore}>{errore}</div>}
        {!righe && <input type="file" accept=".xlsx,.xls" disabled={inCorso} onChange={e => e.target.files[0] && leggi(e.target.files[0])} />}
        {righe && (
          <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
            <table style={stili.table}>
              <thead><tr><th style={stili.th}></th><th style={stili.th}>Riga</th><th style={stili.th}>Esito</th><th style={stili.th}>Voce</th><th style={stili.th}>Soggetto</th><th style={stili.th}>Importo</th><th style={stili.th}>Valido dal</th><th style={stili.th}>Dettaglio</th></tr></thead>
              <tbody>
                {righe.map((x, i) => (
                  <tr key={i} style={stili.tr}>
                    <td style={stili.td}>{['nuova', 'diversa'].includes(x.esito) && <input type="checkbox" aria-label={`Applica riga ${x.riga}`} checked={Boolean(scelte[i])} onChange={e => setScelte(s => ({ ...s, [i]: e.target.checked }))} />}</td>
                    <td style={stili.td}>{x.riga}</td>
                    <td style={stili.td}><span style={{ fontSize: '0.75rem', padding: '0.1rem 0.45rem', borderRadius: '4px', color: ESITI[x.esito][1], background: ESITI[x.esito][2] }}>{ESITI[x.esito][0]}</span></td>
                    <td style={stili.td}>{x.dati.voce}</td>
                    <td style={stili.td}>{SOGGETTI_BREVI[x.dati.soggetto] || '—'}</td>
                    <td style={stili.td}>{formattaEuro(x.dati.importo)}</td>
                    <td style={stili.td}>{x.dati.valido_dal ? formattaData(x.dati.valido_dal) : ''}</td>
                    <td style={{ ...stili.td, fontSize: '0.8rem', color: '#666' }}>{x.motivo || (x.esito === 'diversa' ? `Ora: ${formattaEuro(x.attuale.importo) || 'nessun importo'}` : '')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div style={stili.azioni}>
          <button type="button" onClick={onChiudi} style={stili.btnSecondary}>Annulla</button>
          {righe && <button onClick={applica} disabled={inCorso || scelti === 0} style={stili.btn}>{inCorso ? 'Salvataggio…' : `Applica ${scelti} righe`}</button>}
        </div>
      </div>
    </div>
  );
}

// ---------- collegamento dicitura -> voce (da approvare)

function Collegamenti({ admin }) {
  const [dati, setDati] = useState(null);
  const [voci, setVoci] = useState([]);
  const [scelte, setScelte] = useState({});   // dicitura_id -> voce_id ('' = nessun listino)
  const [crea, setCrea] = useState({});       // voce_id -> true
  const [errore, setErrore] = useState('');
  const [messaggio, setMessaggio] = useState('');
  const [inCorso, setInCorso] = useState(false);

  const carica = useCallback(async () => {
    try {
      const [c, l] = await Promise.all([api.get('/competenze/listino/collegamenti'), api.get('/competenze/listino')]);
      setDati(c.data);
      setVoci([...new Map(l.data.listino.map(r => [r.voce_id, { id: r.voce_id, nome: r.voce }])).values()].sort((a, b) => a.nome.localeCompare(b.nome)));
      setScelte(Object.fromEntries(c.data.diciture.filter(d => d.tipo === 'normale').map(d => [d.dicitura_id, d.voce_attuale_id || d.proposta_voce_id || ''])));
      setCrea(Object.fromEntries(c.data.voci_senza_dicitura.map(v => [v.id, false])));
    } catch (err) { setErrore(err.response?.data?.errore || 'Errore nel caricamento'); }
  }, []);
  useEffect(() => { carica(); }, [carica]);

  if (!dati) return errore ? <div style={stili.errore}>{errore}</div> : <p>Caricamento…</p>;
  const normali = dati.diciture.filter(d => d.tipo === 'normale');
  const collocamento = dati.diciture.filter(d => d.tipo === 'collocamento');

  const applica = async () => {
    setErrore('');
    setInCorso(true);
    try {
      const collegamenti = normali.filter(d => String(scelte[d.dicitura_id] || '') !== String(d.voce_attuale_id || '')).map(d => ({ dicitura_id: d.dicitura_id, voce_id: scelte[d.dicitura_id] ? Number(scelte[d.dicitura_id]) : null }));
      const r = await api.post('/competenze/listino/collegamenti/applica', { collegamenti, crea_diciture: Object.keys(crea).filter(k => crea[k]).map(Number) });
      setMessaggio(`Fatto: ${r.data.collegate} collegamenti, ${r.data.scollegate} tolti, ${r.data.diciture_create} diciture create`);
      carica();
    } catch (err) { setErrore(err.response?.data?.errore || 'Applicazione non riuscita: non è stato salvato niente'); } finally { setInCorso(false); }
  };
  const modifiche = normali.filter(d => String(scelte[d.dicitura_id] || '') !== String(d.voce_attuale_id || '')).length + Object.values(crea).filter(Boolean).length;

  return (
    <>
      <p style={{ color: '#555', fontSize: '0.88rem', marginTop: 0 }}>
        Qui scegli quale voce del listino usa ciascuna dicitura. Le diciture <strong>senza collegamento restano senza importo automatico</strong>. Il collocamento non ha listino né importo: passa da GIS.
        Ho proposto un collegamento solo dove la dicitura coincide quasi per intero con una voce; per le diciture generiche (che coprono più voci) trovi le candidate da scegliere. Nulla cambia finché non applichi.
      </p>
      {errore && <div style={stili.errore}>{errore}</div>}
      {messaggio && <div style={{ background: '#ecfdf5', color: '#166534', padding: '0.6rem', borderRadius: '6px', marginBottom: '0.8rem', fontSize: '0.88rem' }}>{messaggio}</div>}
      <h3 style={{ marginBottom: '0.4rem' }}>Diciture ({normali.length})</h3>
      <table style={stili.table}>
        <thead><tr><th style={stili.th}>Dicitura</th><th style={stili.th}>Categoria</th><th style={stili.th}>Voce di listino</th><th style={stili.th}>Candidate</th></tr></thead>
        <tbody>
          {normali.map(d => (
            <tr key={d.dicitura_id} style={stili.tr}>
              <td style={stili.td}>{d.testo}</td>
              <td style={{ ...stili.td, color: '#666' }}>{d.categoria}</td>
              <td style={stili.td}>
                <select disabled={!admin} value={scelte[d.dicitura_id] ?? ''} onChange={e => setScelte(s => ({ ...s, [d.dicitura_id]: e.target.value }))} style={{ ...stili.input, minWidth: '240px' }} aria-label={`Voce per ${d.testo}`}>
                  <option value="">Nessun listino (nessun importo automatico)</option>
                  {voci.map(v => <option key={v.id} value={v.id}>{v.nome}</option>)}
                </select>
                {d.proposta_voce_id && !d.voce_attuale_id && <small style={{ color: '#166534' }}> proposta</small>}
              </td>
              <td style={{ ...stili.td, fontSize: '0.8rem', color: '#666' }}>
                {d.candidate.map(c => (
                  <div key={c.id}>
                    {admin ? <button type="button" style={{ ...stili.btnSmall, marginRight: '0.3rem' }} onClick={() => setScelte(s => ({ ...s, [d.dicitura_id]: String(c.id) }))}>usa</button> : null}
                    {c.nome} <span style={{ color: '#999' }}>({Math.round(c.punteggio * 100)}%)</span>
                  </div>
                ))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3 style={{ margin: '1.2rem 0 0.4rem' }}>Voci del listino senza dicitura ({dati.voci_senza_dicitura.length})</h3>
      <p style={{ color: '#555', fontSize: '0.85rem', marginTop: 0 }}>Per ogni voce spuntata creo una dicitura con lo stesso nome (categoria "Pratiche e servizi vari") e la collego: <strong>senza causale GIS</strong>, da completare. Le voci senza importo (rinnovi PEC, "da valutare") non sono spuntate.</p>
      <div style={{ columns: '2 320px', fontSize: '0.86rem' }}>
        {dati.voci_senza_dicitura.map(v => (
          <label key={v.id} style={{ display: 'flex', gap: '0.4rem', alignItems: 'flex-start', marginBottom: '0.25rem', breakInside: 'avoid' }}>
            <input type="checkbox" disabled={!admin} checked={Boolean(crea[v.id])} onChange={e => setCrea(c => ({ ...c, [v.id]: e.target.checked }))} />
            <span>{v.nome} <small style={{ color: '#999' }}>{v.categoria}{v.ha_prezzo ? '' : ' · senza importo'}</small></span>
          </label>
        ))}
      </div>
      {admin && admin !== undefined && (
        <div style={{ marginTop: '0.8rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <button onClick={() => setCrea(Object.fromEntries(dati.voci_senza_dicitura.map(v => [v.id, v.ha_prezzo])))} style={stili.btnSecondary}>Spunta tutte quelle con importo</button>
          <button onClick={applica} disabled={inCorso || modifiche === 0} style={stili.btn}>{inCorso ? 'Applico…' : `Applica ${modifiche} modifiche`}</button>
        </div>
      )}

      <details style={{ marginTop: '1.2rem' }}>
        <summary style={{ cursor: 'pointer' }}>Collocamento: nessun listino né importo ({collocamento.length} diciture)</summary>
        <ul style={{ fontSize: '0.86rem', color: '#666' }}>{collocamento.map(d => <li key={d.dicitura_id}>{d.testo}</li>)}</ul>
      </details>
    </>
  );
}
